export type ColumnId =
  | "firstName"
  | "lastName"
  | "title"
  | "url"
  | "location"
  | "company"
  | "department"
  | "country"
  | "seniority"
  | "totalExperienceMonths"
  | "currentCompany"
  | "f1ExperienceMonths"
  | "experienceCount"
  | "hasF1V8Experience"
  | "hasFiaExperience"
  | "hasAusEducation"
  | "hasUsEducation"
  | "pumExperienceMonths"
  | "australiaExperienceMonths"
  | "usaExperienceMonths"
  | "fakeProbability"
  | "education"
  | "companiesWorkedAt"
  | "LastSyncedAt";

export type SortField =
  | "Company"
  | "FullName"
  | "Title"
  | "FirstName"
  | "LastName"
  | "Department"
  | "Country"
  | "Location"
  | "Seniority"
  | "F1ExperienceMonths"
  | "TotalExperienceMonths"
  | "experienceCount"
  | "HasF1V8Experience"
  | "HasFiaExperience"
  | "HasAusEducation"
  | "HasUsEducation"
  | "PumExperienceMonths"
  | "AustraliaExperienceMonths"
  | "UsaExperienceMonths"
  | "FakeProbability"
  | "LinkedInUrl"
  | "CurrentCompany"
  | "Education"
  | "CompaniesWorkedAt"
  | "LastSyncedAt";

export interface ColumnDefinition {
  id: ColumnId;
  label: string;
  sortField: SortField;
  defaultVisible: boolean;
}

export const COMPANY_STAFF_COLUMNS: readonly ColumnDefinition[] = [
  {
    id: "firstName",
    label: "First Name",
    sortField: "FirstName",
    defaultVisible: true,
  },
  {
    id: "lastName",
    label: "Last Name",
    sortField: "LastName",
    defaultVisible: true,
  },
  {
    id: "title",
    label: "Current Position",
    sortField: "Title",
    defaultVisible: true,
  },
  {
    id: "url",
    label: "Profile",
    sortField: "LinkedInUrl",
    defaultVisible: true,
  },
  {
    id: "location",
    label: "Location",
    sortField: "Location",
    defaultVisible: true,
  },
  {
    id: "department",
    label: "Department",
    sortField: "Department",
    defaultVisible: false,
  },
  {
    id: "country",
    label: "Country",
    sortField: "Country",
    defaultVisible: false,
  },
  {
    id: "seniority",
    label: "Seniority",
    sortField: "Seniority",
    defaultVisible: false,
  },
  {
    id: "totalExperienceMonths",
    label: "Experience Total Yrs",
    sortField: "TotalExperienceMonths",
    defaultVisible: false,
  },
  {
    id: "currentCompany",
    label: "Current Company",
    sortField: "CurrentCompany",
    defaultVisible: true,
  },
  {
    id: "f1ExperienceMonths",
    label: "Experience F1 Yrs",
    sortField: "F1ExperienceMonths",
    defaultVisible: true,
  },
  {
    id: "experienceCount",
    label: "Experience Count",
    sortField: "experienceCount",
    defaultVisible: false,
  },
  {
    id: "hasF1V8Experience",
    label: "F1 V8 Experience",
    sortField: "HasF1V8Experience",
    defaultVisible: false,
  },
  {
    id: "hasFiaExperience",
    label: "FIA Experience",
    sortField: "HasFiaExperience",
    defaultVisible: false,
  },
  {
    id: "hasAusEducation",
    label: "AUS Education",
    sortField: "HasAusEducation",
    defaultVisible: false,
  },
  {
    id: "hasUsEducation",
    label: "US Education",
    sortField: "HasUsEducation",
    defaultVisible: false,
  },
  {
    id: "pumExperienceMonths",
    label: "PUM Experience",
    sortField: "PumExperienceMonths",
    defaultVisible: true,
  },
  {
    id: "australiaExperienceMonths",
    label: "Australia Experience Yrs",
    sortField: "AustraliaExperienceMonths",
    defaultVisible: false,
  },
  {
    id: "usaExperienceMonths",
    label: "USA Experience Yrs",
    sortField: "UsaExperienceMonths",
    defaultVisible: false,
  },
  {
    id: "fakeProbability",
    label: "Fake Probability",
    sortField: "FakeProbability",
    defaultVisible: false,
  },
  {
    id: "education",
    label: "Education",
    sortField: "Education",
    defaultVisible: false,
  },
  {
    id: "companiesWorkedAt",
    label: "Companies Worked At",
    sortField: "CompaniesWorkedAt",
    defaultVisible: false,
  },
  {
    id: "LastSyncedAt",
    label: "Last Updated",
    sortField: "LastSyncedAt",
    defaultVisible: false,
  },
] as const;

const createColumnToSortFieldMap = (): Record<ColumnId, SortField> => {
  const map = {} as Record<ColumnId, SortField>;
  for (const col of COMPANY_STAFF_COLUMNS) {
    map[col.id] = col.sortField;
  }
  return map;
};

export const COLUMN_TO_SORT_FIELD_MAP: Record<ColumnId, SortField> =
  createColumnToSortFieldMap();

export const SORT_FIELD_TO_COLUMN_MAP: Record<SortField, ColumnId> =
  COMPANY_STAFF_COLUMNS.reduce(
    (acc, col) => {
      acc[col.sortField] = col.id;
      return acc;
    },
    {} as Record<SortField, ColumnId>,
  );

export const COLUMN_LABELS: Record<ColumnId, string> =
  COMPANY_STAFF_COLUMNS.reduce(
    (acc, col) => {
      acc[col.id] = col.label;
      return acc;
    },
    {} as Record<ColumnId, string>,
  );

export const DEFAULT_COLUMN_VISIBILITY: Record<ColumnId, boolean> =
  COMPANY_STAFF_COLUMNS.reduce(
    (acc, col) => {
      acc[col.id] = col.defaultVisible;
      return acc;
    },
    {} as Record<ColumnId, boolean>,
  );

export type ColumnVisibility = Record<ColumnId, boolean>;

export interface ColumnConfig {
  id: ColumnId;
  label: string;
}

export const AVAILABLE_COLUMNS: readonly ColumnConfig[] =
  COMPANY_STAFF_COLUMNS.map((col) => ({
    id: col.id,
    label: col.label,
  }));
